// src/api/sdhAPI_fieldContext.ts
// UI.2 — the three calls SdhReviewWorkspace_v2 needs that sdhAPI.ts does not provide.
// Additive module; sdhAPI.ts is untouched.
import { apiGet } from "../API";

// Matches sdhAPI.ts line 6 exactly.
const BASE = "/adriva_api/dq/sdh";

/* ── per-field knowledge ─────────────────────────────────────────────────── */
export interface EkdFieldView {
  field_id: string; table: string; column: string;
  display_name?: string; short_description?: string; long_description?: string;
  metadata_authority?: string;            // declared | dictionary_confirmed | dictionary_reconciled
  data_type?: string; semantic_role?: string;
  semantic_roles?: Array<{ role: string; confidence: number; confirmation_required?: boolean;
                           signals?: Array<{ name: string; value: any; weight: number; source: string }> }>;
  nullable_pct?: number; distinct_count?: number; record_count?: number; uniqueness_ratio?: number;
  reference_domain_ids?: string[]; linked_fields?: string[];
  dictionary_name?: string; prior_display_name?: string; prior_short_description?: string;
  business_context?: string; business_responsibility?: string; sap_view?: string;
  // UI.3 — free text on 16 of the 559 fields, absent on the rest. It was typed as string[]
  // and the panel called .join() on it, which crashed the workspace on exactly those 16.
  // Both shapes are accepted and the panel renders it through asText().
  known_gaps?: string | string[];
  evidence?: Array<{ evidence_id: string; kind: string; authority: string;
                     source_id: string; statement: string }>;
}

export interface RefDomainView {
  domain_id?: string; name?: string;
  // `source` was always null: the artifact key is `source_system`. Kept as an alias.
  source?: string; source_system?: string;
  source_ref?: { store?: string; entity_id?: string } | null;
  authority?: string; status?: string; promoted_value_count?: number;
  values: Array<{ code: string; display?: string; description?: string;
                  governance_status?: string }>;
}

/** An artifact-level knowledge gap naming this field. `recommended_question` is what the SME
 *  is being asked to resolve. */
export interface KnowledgeGapView {
  gap_id?: string; gap_type?: string; severity?: string;
  description?: string; affected_objects?: string[]; recommended_question?: string;
}

// UI.3 — the endpoint now returns the control WHOLE rather than a fixed projection, because
// the projection silently dropped challenge_issues, readiness_assessment.blocking_reasons,
// participating_fields[].role, derivation.rationale, the control's own evidence and the lane's
// sme_question — all of which answer "why is there no rule here". The named keys below are the
// ones the panel reads; the index signature carries the rest so a schema addition shows up
// without a type change.
export interface BcControlSummary {
  control_id: string; origin?: string; control_kind?: string; family?: string;
  category?: string; title?: string; business_statement?: string; business_intent?: string;
  business_reason?: string; condition?: string; expected_outcome?: string;
  condition_code?: string; condition_code_meaning?: string;
  predicate_template?: string | null; business_expression?: string | null;
  confidence?: number; evidence_type?: string; authority?: string;
  lane?: number; lane_name?: string; readiness?: string; disposition?: string;
  mapping_status?: string; action_type?: string; execution_scope?: string;
  rule_generation_eligible?: boolean; rule_generation_exclusion_reason?: string | null;
  execution_ready?: boolean; requires_reference_data?: boolean;
  owner_field_id?: string | null;
  participating_fields?: any[]; grain_fields?: string[]; target_tables?: string[];
  target_entities?: string[]; join_conditions?: any[]; reference_data?: any[] | null;
  parameters?: any[]; evidence?: any[]; clause_ids?: string[]; tags?: string[];
  business_impact?: string[]; inferred_details?: any[]; exceptions?: any[];
  applicability?: any; execution_specification?: any; derivation?: any; knowledge_links?: any;
  readiness_assessment?: any; challenge_issues?: any[]; quality_dimensions?: any;
  // quality_issues is a list of plain strings in this artifact, not objects.
  quality_issues?: any[];
  evidence_score?: number; structure_score?: number; quality_score?: number;
  semantic_confidence?: number; configuration_score?: number; approval_score?: number;
  simulation_score?: number | null; overall_readiness_score?: number;
  // Lifted server-side out of extensions.lane so the UI need not know where they live.
  sme_question?: string | null; lane_state?: string | null;
  lane_needs?: string | null; lane_reason?: string | null;
  [k: string]: any;
}

export interface FieldContext {
  success: boolean; field_id: string;
  ekd: EkdFieldView | null; ekd_present: boolean;
  reference_domains: RefDomainView[];
  business_controls: BcControlSummary[];
  knowledge_gaps: KnowledgeGapView[];
  counts: { domains: number; controls: number; knowledge_gaps: number; governed_values: number };
}

/** EKD + governed values + BC controls for ONE field, in one round-trip. */
export function sdh_fieldContext(entityId: string, table: string, column: string): Promise<FieldContext> {
  return apiGet(`${BASE}/${encodeURIComponent(entityId)}/field-context/${encodeURIComponent(table)}/${encodeURIComponent(column)}`);
}

/* ── rule authorship ─────────────────────────────────────────────────────── */
// `/sdh/{id}/rules` exposes `_raw`, but that is the v1 FLAT rule which has no `versions[]`,
// so created_by / trigger are unreachable through it. This side-map is joined by rule_id.
export interface RuleAuthorship {
  created_by?: string; trigger?: string; change_type?: string; change_sub_type?: string;
  current_version: number; max_version: number;
  has_open_proposal: boolean;             // a newer version exists with no decision recorded
  lifecycle?: string; owner_field_id?: string;
}

export function sdh_ruleAuthorship(entityId: string):
  Promise<{ success: boolean; rules: Record<string, RuleAuthorship>; count: number }> {
  return apiGet(`${BASE}/${encodeURIComponent(entityId)}/rule-authorship`);
}

/* ── full profile column ─────────────────────────────────────────────────── */
// The existing sdh_dataProfileColumn type omits everything Batch A added. Same endpoint,
// widened response (UI.2 P1) — the extra keys are additive, so the old type still holds.
export interface ProfileColumnFull {
  success: boolean; table: string; column: string;
  data_type?: string; recommended_type?: string;
  count?: number; records?: number;
  null_percentage?: number; null_pct?: number; non_null_density?: number;
  unique_values?: number; distinct?: number;
  distinct_values?: Record<string, number>;
  distinct_values_total?: number; distinct_values_listed?: number;
  distinct_values_truncated?: boolean;   // the value list is CAPPED — not a closed set
  distinct_values_cap?: number;
  numeric_stats?: { min?: number; max?: number; mean?: number };
  numeric_format?: string; numeric_parse_rate?: number;
  data_length_stats?: { min_length?: number; max_length?: number };
  value_type_counts?: Record<string, number>;
  top_values?: Array<{ value: string; count: number }>;
}

export function sdh_profileColumnFull(entityId: string, table: string, column: string, top = 200):
  Promise<ProfileColumnFull> {
  return apiGet(`${BASE}/${encodeURIComponent(entityId)}/data-profile/${encodeURIComponent(table)}/${encodeURIComponent(column)}?top=${top}`);
}

/* ── UI.3: batch per-table field summary ─────────────────────────────────── */
// `sdh_fieldContext` above resolves ONE field. The sidebar renders every field in the table at
// once, so with only that call every card except the selected one read "unknown". This returns
// the five facts a card shows, for the whole table, in one round-trip — cheap enough to fetch
// on table change and cache for the session.
export interface FieldSummary {
  display_name?: string | null;
  semantic_role?: string | null;
  data_type?: string | null;
  metadata_authority?: string | null;
  // Governed value sets resolved from the DOMAIN side — the same count /field-context
  // reports, so the sidebar and the panel it opens can never disagree.
  domain_count: number;
  // The field's own reference_domain_ids. Usually the same relationship seen from the other
  // end, so it is kept separate rather than summed.
  declared_domains?: number;
  control_count: number;
  // Open knowledge gaps naming this field — a reason it may have no usable rule.
  gap_count?: number;
  ekd_present: boolean;
}

export function sdh_fieldContextSummary(entityId: string, table: string):
  Promise<{ success: boolean; table: string; fields: Record<string, FieldSummary>; count: number }> {
  return apiGet(`${BASE}/${encodeURIComponent(entityId)}/field-context-summary/${encodeURIComponent(table)}`);
}

/* ── UI.3: configured field scope ────────────────────────────────────────── */
// Reads config/dq/scope/<entity_id>.yml through the SAME resolver the pipeline uses, so the
// sidebar's priority list cannot drift from the scope the rules were generated under.
export interface ScopeInfo {
  success: boolean;
  mode?: string | null;                 // full_model | explicit | dq_story
  fields: string[] | null;              // null = full model (every profiled field in scope)
  field_count?: number;
  reason?: string;
  unresolved: string[];                 // configured but matching no profiled column
  fanned_out?: Record<string, string[]>;
  source?: string | null;
  error?: string;
}

export function sdh_scope(entityId: string): Promise<ScopeInfo> {
  return apiGet(`${BASE}/${encodeURIComponent(entityId)}/scope`);
}

/* ── F2.6: knowledge → control → rule traceability ───────────────────────── */
// One inversion of rule.reference.business_control_id, plus the EKD-dependency reverse for
// no_control rules. Read-only. See the /sdh/{id}/traceability endpoint.
export interface TraceControl {
  eligible?: boolean | null;
  control_kind?: string;
  exclusion_reason?: string | null;
  owner_field_id?: string | null;
  title?: string | null;
  rules: Array<{ rule_id: string; lifecycle?: string | null; owner_field_id?: string | null }>;
}
export interface TraceRule {
  control_id?: string | null;
  lifecycle?: string | null;
  owner_field_id?: string | null;
  ekd_dependency?: { statement?: string; eligibility?: string; confirmed?: boolean } | null;
}
export interface TraceDependency {
  statement?: string; endpoints?: string[]; evidence_type?: string;
  confirmed?: boolean; eligibility?: string;
  inferred_control_id?: string | null; rule_ids?: string[];
}
export interface Traceability {
  success: boolean;
  controls: Record<string, TraceControl>;
  rules: Record<string, TraceRule>;
  dependencies: TraceDependency[];
  counts: {
    rules: number; rules_with_control: number; rules_no_control: number;
    controls_total: number; controls_producing_rules: number; controls_eligible_no_rule: number;
  };
}
export function sdh_traceability(entityId: string): Promise<Traceability> {
  return apiGet(`${BASE}/${encodeURIComponent(entityId)}/traceability`);
}
